import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { Cell, Column, Table } from 'fixed-data-table-2';
import TextCell from '../../../components/TextCell/TextCell';
import { DateType } from '../../../containers/UIhelper/UIDateFormater';
import { Button } from '@mui/material';
import AppModal from '../../Modals/AppModal';
import Plot from 'react-plotly.js';
import { downloadCsv, formatCsvTimestamp, resolveCsvTimeZone } from '../../../utils/csvDownload';

const _ = require('lodash');

const PRESSURE_CONVERSION = {
  meter: { factor: 1, label: 'Meter' },
  bar: { factor: 0.09804139432, label: 'Bar' },
  kpa: { factor: 9.80665, label: 'kPa' },
  psi: { factor: 1.42233, label: 'PSI' },
};

// Minimum Y-axis range of 3 bar (converted to the active unit at render time).
const MIN_RANGE_BAR = 3;

require('../SensorInfo/SensorInfo.scss');

class PressureSensorTransientTab extends React.Component {
  constructor(props) {
    super(props);

    this.onRowClick = this.onRowClick.bind(this);
    this.rowClassNameGetter = this.rowClassNameGetter.bind(this);

    this.state = {
      readingData: [],
      readingRaw: [],
      activeTransient: null,
      rowLoading: undefined
    }
  }

  componentDidMount() {
    this.tryFetch();
  }

  componentDidUpdate(prevProps) {
    const { projectId, loggerId } = this.props;
    if (projectId !== prevProps.projectId || loggerId !== prevProps.loggerId) {
      this.tryFetch();
    }
  }

  tryFetch() {
    const { projectId, loggerId, fetchTransients } = this.props;
    if (projectId && loggerId && fetchTransients) {
      fetchTransients(projectId, loggerId);
    }
  }

  onRowClick(event, rowIndex) {
    const { transients, selectTransient } = this.props;
    if (selectTransient) {
      selectTransient(transients[rowIndex]);
    }
  }

  rowClassNameGetter(rowIndex) {
    const { selectedTransient, transients } = this.props;
    return selectedTransient?.event_time &&
      transients[rowIndex] &&
      selectedTransient.event_time === transients[rowIndex].event_time
      ? 'active-row'
      : '';
  }

  handleDownloadTransientCsv = () => {
    const { pressureUnit, loggerId, timeZone, projectTimeZone } = this.props;
    const { readingRaw, activeTransient } = this.state;
    if (!readingRaw?.length) return;
    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
    const unitLabel = conversion.label.toLowerCase();
    const { ianaTimeZone, label: tzLabel } = resolveCsvTimeZone(timeZone, projectTimeZone);
    const headers = [`timestamp (${tzLabel})`, `pressure_${unitLabel}`];
    const rows = readingRaw.map((r) => [
      formatCsvTimestamp(r.time_utc, ianaTimeZone, { includeMillis: true }),
      r.measurement_m != null ? (parseFloat(r.measurement_m) * conversion.factor).toFixed(4) : '',
    ]);
    const fmtVal = (v) => v != null ? (parseFloat(v) * conversion.factor).toFixed(4) : '';
    const eventTimeFormatted = activeTransient?.event_time
      ? formatCsvTimestamp(activeTransient.event_time, ianaTimeZone, { includeMillis: true })
      : '';
    const preambleLines = [
      `# event_time (${tzLabel}),min_${unitLabel},max_${unitLabel}`,
      `${eventTimeFormatted},${fmtVal(activeTransient?.min_reading)},${fmtVal(activeTransient?.max_reading)}`,
    ];
    const eventTag = eventTimeFormatted
      ? eventTimeFormatted.replace(/[: .]/g, '_')
      : new Date().toISOString().split('T')[0];
    const filename = `pressure_transient_sensor_${loggerId || 'unknown'}_${eventTag}.csv`;
    downloadCsv(filename, headers, rows, preambleLines);
  };

  render() {
    const {
      transients,
      uiInfo,
      projectTimeZone,
      timeZone,
      isFetching,
      pressureUnit,
    } = this.props;

    const pWidth = 0.395 * (uiInfo.pageWidth - 10);
    const pHeight = 0.494 * uiInfo.pageHeight - 8;

    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
    const rawData = transients !== null && !_.isEmpty(transients) ? transients : [];
    const data = rawData.map((t) => ({
      ...t,
      min_reading: t.min_reading != null ? (parseFloat(t.min_reading) * conversion.factor).toFixed(2) : t.min_reading,
      max_reading: t.max_reading != null ? (parseFloat(t.max_reading) * conversion.factor).toFixed(2) : t.max_reading,
    }));

    if (isFetching) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p>{this.context.t('loading')}</p>
        </div>
      );
    }

    return (
      <div>
        <Table
          rowsCount={data.length}
          width={pWidth}
          onRowClick={this.onRowClick}
          rowClassNameGetter={this.rowClassNameGetter}
          maxHeight={pHeight - 60}
          headerHeight={46}
          rowHeight={30}
        >
          <Column
            columnKey='event_time'
            header={<Cell>{this.context.t('timestamp')}</Cell>}
            cell={
              <TextCell
                data={data}
                field='event_time'
                dateType={DateType.DATE_AND_TIME_WITH_SECONDS}
                additional={timeZone}
                projectTimezone={projectTimeZone}
              />
            }
            width={150}
            flexGrow={1}
          />
          <Column
            columnKey='min_reading'
            header={<Cell>{`${this.context.t('min')} (${conversion.label})`}</Cell>}
            cell={<TextCell data={data} field='min_reading' />}
            width={100}
            flexGrow={1}
            align='center'
          />
          <Column
            columnKey='max_reading'
            header={<Cell>{`${this.context.t('max')} (${conversion.label})`}</Cell>}
            cell={<TextCell data={data} field='max_reading' />}
            width={100}
            flexGrow={1}
            align='center'
          />
          <Column
            columnKey='max_reading'
            header={<Cell>{this.context.t('graph')}</Cell>}
            cell={({ rowIndex }) => <Cell style={{ width: '100%' }}>
              <Button
                variant='contained'
                size='small'
                disabled={this.state.rowLoading === rowIndex}
                onClick={async () =>  {
                  console.log(this.props);
                  
                  this.setState({ rowLoading: rowIndex })
                  const { projectId, loggerId } = this.props;
                  const transientRow = this.props.transients[rowIndex];
                  const { reading_id } = data[rowIndex];
                  const readingData = await this.props.fetchTransientReading(projectId, loggerId, reading_id)

                  this.setState({ rowLoading: undefined })

                  if (!readingData.status || !readingData.data?.length) {
                    console.log(readingData);
                    this.props.notify('Failed to load transient readings', 'error')
                  } else {
                    const { data: readData } = readingData;
                    const x = [];
                    const y = [];

                    readData.forEach((read) => {
                      x.push(new Date(read.time_utc))
                      y.push(read.measurement_m)
                    })
                    const plotData = [{
                      x, y,
                      mode: 'lines',
                      type: 'scatter',
                      line: {
                        shape: 'spline',
                        // smoothing: 1.3,
                        width: 3
                      }
                    }]

                    this.setState({
                      readingData: plotData,
                      readingRaw: readData,
                      activeTransient: transientRow,
                    });
                  }
                }}>{this.context.t('open')}</Button>
            </Cell>}
            width={15}
            flexGrow={1}
            align='center'
          />
        </Table>
        <AppModal open={this.state.readingData.length !== 0} content={
        <>
        <Plot
          data={this.state.readingData.map((trace) => ({
            ...trace,
            y: (trace.y || []).map((y) => (y == null || isNaN(y) ? y : Number((y * conversion.factor).toFixed(2)))),
          }))}
          layout={{
            margin: { t: 0, b: 40 },
            xaxis: {
              hoverformat: '%Y-%m-%d %H:%M:%S.%L',
              tickformatstops: [
                { dtickrange: [null, 1000], value: '%H:%M:%S.%L' },
                { dtickrange: [1000, 60000], value: '%H:%M:%S' },
                { dtickrange: [60000, 3600000], value: '%H:%M' },
                { dtickrange: [3600000, 86400000], value: '%H:%M\n%b %d' },
                { dtickrange: [86400000, null], value: '%b %d' },
              ],
            },
            yaxis: (() => {
              const minRangeInUnit = MIN_RANGE_BAR * (conversion.factor / PRESSURE_CONVERSION.bar.factor);
              const ys = (this.state.readingData?.[0]?.y || [])
                .filter((v) => v != null && !isNaN(v))
                .map((v) => v * conversion.factor);
              if (!ys.length) return {};
              const yMin = Math.min(...ys);
              const yMax = Math.max(...ys);
              if (yMax - yMin >= minRangeInUnit) return {};
              const center = (yMin + yMax) / 2;
              return { range: [center - minRangeInUnit / 2, center + minRangeInUnit / 2], autorange: false };
            })(),
          }}
        />
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <Button
            variant='contained'
            onClick={() => this.setState({ readingData: [], readingRaw: [], activeTransient: null })}
          >{this.context.t('close')}</Button>
          <Button
            variant='outlined'
            color='primary'
            onClick={this.handleDownloadTransientCsv}
            disabled={!this.state.readingRaw?.length}
          >{`${this.context.t('download')} CSV`}</Button>
        </div>
        </>
        }/>
      </div>
    );
  }
}

PressureSensorTransientTab.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default connect((state) => ({
  pressureUnit: state.local.pressureUnit || 'meter',
}))(PressureSensorTransientTab);
